/**
 * A client's module page — the step between the client and its work.
 *
 * Clients → {client} → this page (`/clients/:id/modules`) → the module's hub
 * (Documents / Audits). One large card per module, always a link: its name,
 * what it covers, and the client's counts in it. Every client is in every
 * module.
 *
 * The page names no module of its own: a `module` already in the URL changes
 * nothing here.
 */

import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../provider/auth';
import { PageHeader } from '../../provider/pageTitle';
import { useClientQuery } from '../../queries/intake';
import { useEveryModuleClientsQuery } from '../../queries/clients';
import {
	createPageHeaderSubTitleForClients,
	ErrorNotice,
} from '../../shared/ui';
import { ErrorState, LoadingState } from '../shared/states';
import {
	MODULE_BLURBS,
	MODULE_CODES,
	MODULE_LABELS,
	type ModuleCode,
} from './modules';
import {
	flowAccess,
	hubPath,
	modulePagePath,
	moduleTally,
	tallyText,
} from './flow';

/* The card frame: shared by the module cards and the hub's. */
const CARD_SX = {
	display: 'flex',
	flexDirection: 'column',
	gap: 'var(--s3)',
	minWidth: 0,
	p: 'var(--s4)',
	bgcolor: 'var(--surface)',
	border: '1px solid var(--line)',
	borderRadius: '12px',
	boxShadow: 'var(--e1)',
	color: 'inherit',
	textDecoration: 'none',
} as const;

/** The grid both pages lay their cards in: compact cards, never stretched. */
export const CARD_GRID_SX = {
	listStyle: 'none',
	m: 0,
	p: 0,
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 360px))',
	gap: 'var(--s4)',
} as const;

const MODULE_ICONS: Record<ModuleCode, ReactNode> = {
	O2C: <ReceiptLongOutlinedIcon />,
	H2R: <BadgeOutlinedIcon />,
};

export default function ClientModulesPage() {
	const { clientId = '' } = useParams();
	const { permissions } = useAuth();
	const access = flowAccess(permissions);
	const client = useClientQuery(clientId);
	const everyModule = useEveryModuleClientsQuery();
	const root = { label: 'Clients', to: '/clients' };

	if (client.isPending) return <LoadingState label="Loading the client…" />;
	if (client.error || !client.data) {
		return (
			<Box className="stack">
				<PageHeader breadcrumbs={[root]} title="Choose a module" />
				<ErrorState
					error={
						client.error ?? new Error('This client was not found.')
					}
					onRetry={client.refetch}
					hint={
						<>
							Every client is listed on{' '}
							<Link className="link" to={root.to}>
								{root.label}
							</Link>
							.
						</>
					}
				/>
			</Box>
		);
	}

	const record = client.data;

	return (
		<Box className="stack">
			<PageHeader
				breadcrumbs={[
					root,
					{ label: record.name, to: modulePagePath(clientId) },
				]}
				title={record.name}
				subtitle={createPageHeaderSubTitleForClients(
					record.code,
					record.gstin,
				)}
			/>

			<ErrorNotice error={everyModule.error} />

			<Box
				component="ul"
				aria-label="Modules"
				sx={{
					...CARD_GRID_SX,
				}}
			>
				{MODULE_CODES.map((code) => {
					const tally = moduleTally(
						everyModule.lists[MODULE_CODES.indexOf(code)],
						clientId,
					);
					return (
						<Box
							component="li"
							key={code}
							sx={{ display: 'flex', minWidth: 0 }}
						>
							<ModuleCard
								code={code}
								to={hubPath(clientId, code)}
								detail={
									tally === null
										? '…'
										: tallyText(tally, access)
								}
							/>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
}

/** The icon tile, name and line every card starts with: a module's by
 *  default, or the hub's own icon, title and blurb. */
function CardHead({
	code,
	icon,
	title,
	blurb,
}: {
	code?: ModuleCode;
	icon?: ReactNode;
	title?: string;
	blurb?: string;
}) {
	const glyph = icon ?? (code ? MODULE_ICONS[code] : null);
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'flex-start',
				gap: 'var(--s3)',
				minWidth: 0,
			}}
		>
			{glyph && (
				<Box
					aria-hidden="true"
					sx={{
						flex: '0 0 auto',
						display: 'grid',
						placeItems: 'center',
						width: 40,
						height: 40,
						borderRadius: '10px',
						border: '1px solid var(--accent-border)',
						bgcolor: 'var(--accent-surface)',
						color: 'var(--accent)',
						'& svg': { fontSize: 22 },
					}}
				>
					{glyph}
				</Box>
			)}
			<Box sx={{ minWidth: 0, flex: 1 }}>
				<Typography
					component="h2"
					sx={{
						fontSize: 'var(--fs-16)',
						fontWeight: 600,
						color: 'var(--ink)',
						m: 0,
					}}
				>
					{title ?? (code ? MODULE_LABELS[code] : '')}
				</Typography>
				<Typography
					component="p"
					sx={{
						fontSize: 'var(--fs-body)',
						color: 'var(--ink-3)',
						m: 0,
						mt: '2px',
					}}
				>
					{blurb ?? (code ? MODULE_BLURBS[code] : '')}
				</Typography>
			</Box>
		</Box>
	);
}

/**
 * One compact card, a link: the module page uses it for modules, the hub for
 * Documents and Audits.
 */
export function ModuleCard({
	code,
	icon,
	title,
	blurb,
	to,
	detail,
}: {
	code?: ModuleCode;
	icon?: ReactNode;
	title?: string;
	blurb?: string;
	to: string;
	detail: string;
}) {
	return (
		<Box
			component={Link}
			to={to}
			sx={{
				...CARD_SX,
				flex: 1,
				font: 'inherit',
				textAlign: 'left',
				cursor: 'pointer',
				transition:
					'border-color var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease)',
				'&:hover': {
					borderColor: 'var(--accent-border)',
					boxShadow: 'var(--e2)',
				},
				'&:active': {
					boxShadow: 'var(--e1)',
					transform: 'translateY(1px)',
				},
				'&:hover .go, &:focus-visible .go': {
					color: 'var(--accent)',
					transform: 'translateX(2px)',
				},
				'&:focus-visible': {
					outline: '2px solid var(--focus)',
					outlineOffset: '2px',
					borderColor: 'var(--accent)',
				},
				'@media (prefers-reduced-motion: reduce)': {
					transition: 'none',
					'& .go': {
						transition: 'none',
						transform: 'none !important',
					},
				},
			}}
		>
			<CardHead code={code} icon={icon} title={title} blurb={blurb} />
			<Box
				sx={{
					mt: 'auto',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 'var(--s2)',
				}}
			>
				<Typography
					component="span"
					sx={{
						px: 'var(--s2)',
						py: '2px',
						borderRadius: 'var(--r-full)',
						bgcolor: 'var(--surface-sunken)',
						border: '1px solid var(--line)',
						fontSize: 'var(--fs-12)',
						color: 'var(--ink-2)',
					}}
				>
					{detail}
				</Typography>
				<Box
					component="span"
					className="go"
					aria-hidden="true"
					sx={{
						display: 'inline-flex',
						color: 'var(--ink-3)',
						transition:
							'color var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease)',
					}}
				>
					<ChevronRightRoundedIcon fontSize="small" />
				</Box>
			</Box>
		</Box>
	);
}
